import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Grid,
  Typography,
  Box,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  Card,
  CardContent,
  Chip,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraAltIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FolderCopy as FolderCopyIcon,
  Draw as DrawIcon,
} from '@mui/icons-material';
import {
  getEmployee,
  updateEmployee,
  addObservation,
  uploadPhoto,
  uploadContract,
  uploadDocument,
  deleteDocument,
  BASE_URL,
  createEmployeeSignatureRequest,
  updateEmployeeSignatureStatus,
} from '../services/api';
import PhotoCapture from '../components/PhotoCapture';
import { format } from 'date-fns';

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [observation, setObservation] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [newSigTitle, setNewSigTitle] = useState('');

  useEffect(() => {
    loadEmployee();
  }, [id]);

  const loadEmployee = async () => {
    try {
      const { data } = await getEmployee(id);
      setEmployee(data);
      setEditData(data);
    } catch (error) {
      console.error('Error al cargar empleado:', error);
    }
  };

  const handlePhotoCapture = async (file) => {
    try {
      await uploadPhoto(id, file);
      loadEmployee();
    } catch (error) {
      console.error('Error al subir foto:', error);
      alert(error.response?.data?.message || 'Error al subir foto');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await uploadPhoto(id, file);
        loadEmployee();
      } catch (error) {
        console.error('Error al subir foto:', error);
        alert(error.response?.data?.message || 'Error al subir foto');
      }
    }
    e.target.value = '';
  };

  const handleContractUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await uploadContract(id, file);
        loadEmployee();
        alert('Contrato subido exitosamente');
      } catch (error) {
        console.error('Error al subir contrato:', error);
        alert('Error al subir contrato');
      }
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const name = prompt('Nombre del documento:', file.name);
      if (name) {
        try {
          await uploadDocument(id, file, name);
          loadEmployee();
          alert('Documento subido exitosamente');
        } catch (error) {
          console.error('Error al subir documento:', error);
          alert('Error al subir documento');
        }
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (window.confirm('¿Eliminar este documento?')) {
      try {
        await deleteDocument(id, docId);
        loadEmployee();
      } catch (error) {
        console.error('Error al eliminar documento:', error);
        alert('Error al eliminar documento');
      }
    }
  };

  const handleAddObservation = async () => {
    if (observation.trim()) {
      try {
        await addObservation(id, observation);
        setObservation('');
        loadEmployee();
      } catch (error) {
        console.error('Error al agregar observación:', error);
        alert('Error al agregar observación');
      }
    }
  };

  const handleUpdate = async () => {
    try {
      await updateEmployee(id, editData);
      setEditing(false);
      loadEmployee();
      alert('Empleado actualizado exitosamente');
    } catch (error) {
      console.error('Error al actualizar empleado:', error);
      alert(error.response?.data?.message || 'Error al actualizar empleado');
    }
  };

  const handleCreateSignature = async () => {
    const title = newSigTitle.trim();
    if (!title) return;
    try {
      const { data } = await createEmployeeSignatureRequest(id, title);
      setEmployee(data);
      setEditData(data);
      setNewSigTitle('');
      setSigDialogOpen(false);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Error al crear solicitud de firma');
    }
  };

  const handleSignatureStatusUpdate = async (sigId, status) => {
    try {
      const { data } = await updateEmployeeSignatureStatus(id, sigId, status);
      setEmployee(data);
      setEditData(data);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Error al actualizar firma');
    }
  };

  if (!employee) {
    return <Typography>Cargando...</Typography>;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/employees')}>
          Volver
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={employee.photo ? `${BASE_URL}/${employee.photo}` : undefined}
              sx={{ width: 150, height: 150, margin: '0 auto', mb: 2 }}
            >
              {employee.firstName[0]}{employee.lastName[0]}
            </Avatar>
            <Typography variant="h5" gutterBottom>
              {employee.firstName} {employee.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {employee.position}
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="photo-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Subir Foto
                </Button>
              </label>
              <Button
                variant="outlined"
                startIcon={<CameraAltIcon />}
                fullWidth
                onClick={() => setOpenCamera(true)}
              >
                Tomar Foto
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" gutterBottom>
                <strong>Email:</strong> {employee.email}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Teléfono:</strong> {employee.phone || 'N/A'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Número empleado (terminal):</strong> {employee.employeeNumber || 'Sin asignar'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Sucursal:</strong> {employee.branch?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Fecha de Contratación:</strong>{' '}
                {format(new Date(employee.hireDate), 'dd/MM/yyyy')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Estado:</strong> {employee.status}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Información del Empleado</Typography>
              {!editing ? (
                <Button startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                  Editar
                </Button>
              ) : (
                <Box>
                  <Button onClick={() => setEditing(false)} sx={{ mr: 1 }}>
                    Cancelar
                  </Button>
                  <Button variant="contained" onClick={handleUpdate}>
                    Guardar
                  </Button>
                </Box>
              )}
            </Box>

            {editing ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={editData.firstName}
                    onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Apellido"
                    value={editData.lastName}
                    onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Puesto"
                    value={editData.position}
                    onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Salario"
                    type="number"
                    value={editData.salary}
                    onChange={(e) => setEditData({ ...editData, salary: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Número de empleado (terminal)"
                    value={editData.employeeNumber ?? ''}
                    onChange={(e) =>
                      setEditData({ ...editData, employeeNumber: e.target.value.replace(/\s/g, '') })
                    }
                    helperText="Código único para registrar entrada y salida"
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Haga clic en "Editar" para modificar la información
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contrato
            </Typography>
            {employee.contract ? (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Contrato actual: 
                  <a href={`${BASE_URL}/${employee.contract}`} target="_blank" rel="noopener noreferrer">
                    {' '}Ver contrato
                  </a>
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No hay contrato subido
              </Typography>
            )}
            <input
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="contract-upload"
              type="file"
              onChange={handleContractUpload}
            />
            <label htmlFor="contract-upload">
              <Button component="span" variant="outlined" startIcon={<UploadIcon />}>
                {employee.contract ? 'Actualizar Contrato' : 'Subir Contrato'}
              </Button>
            </label>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Documentos
            </Typography>
            <List>
              {employee.documents?.map((doc) => (
                <ListItem
                  key={doc._id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleDeleteDocument(doc._id)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      doc.path ? (
                        <Link
                          href={`${BASE_URL}/${doc.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          {doc.name}
                        </Link>
                      ) : (
                        doc.name
                      )
                    }
                    secondary={format(new Date(doc.uploadDate), 'dd/MM/yyyy HH:mm')}
                  />
                </ListItem>
              ))}
            </List>
            <input
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              id="document-upload"
              type="file"
              onChange={handleDocumentUpload}
            />
            <label htmlFor="document-upload">
              <Button component="span" variant="outlined" startIcon={<UploadIcon />}>
                Agregar Documento
              </Button>
            </label>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <FolderCopyIcon color="primary" />
              <Typography variant="h6">Expediente ampliado y firmas digitales</Typography>
              <Chip label="Persistente en servidor" size="small" color="success" variant="outlined" />
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
              Etiquetas sugeridas para clasificar documentos; las solicitudes de firma quedan registradas en la base de datos con estado y auditoría básica.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Carpetas sugeridas (etiquetas)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Úsalas como referencia al subir archivos en «Documentos» y «Contrato» arriba.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                  {[
                    'Identificación oficial',
                    'Contratos y anexos',
                    'Evaluaciones de desempeño',
                    'Estudios / capacitación',
                    'Expediente médico',
                  ].map((tag) => (
                    <Chip key={tag} label={tag} variant="outlined" />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DrawIcon fontSize="small" /> Firmas digitales
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  fullWidth
                  sx={{ mb: 1.5 }}
                  onClick={() => setSigDialogOpen(true)}
                >
                  Solicitar firma / documento
                </Button>
                {(employee.digitalSignatures || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin solicitudes registradas.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {employee.digitalSignatures.map((sig) => (
                      <Box
                        key={sig._id}
                        sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {sig.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Estado: {sig.status}
                          {sig.requestedBy?.name ? ` · Solicitó: ${sig.requestedBy.name}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {sig.requestedAt
                            ? `Alta: ${format(new Date(sig.requestedAt), 'dd/MM/yyyy HH:mm')}`
                            : ''}
                          {sig.signedAt
                            ? ` · Firmado: ${format(new Date(sig.signedAt), 'dd/MM/yyyy HH:mm')}`
                            : ''}
                        </Typography>
                        {sig.status === 'pending' ? (
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{ mt: 1.5 }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleSignatureStatusUpdate(sig._id, 'signed')}
                            >
                              Registrar firmado
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleSignatureStatusUpdate(sig._id, 'cancelled')}
                            >
                              Cancelar solicitud
                            </Button>
                          </Stack>
                        ) : (
                          <Chip
                            size="small"
                            sx={{ mt: 1 }}
                            label={sig.status === 'signed' ? 'Firmado' : 'Cancelado'}
                            color={sig.status === 'signed' ? 'success' : 'default'}
                          />
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Observaciones y notas de administración
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Registro cronológico visible para roles autorizados (equivalente a notas internas de RRHH).
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Agregar observación..."
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleAddObservation}
                sx={{ mt: 1 }}
                disabled={!observation.trim()}
              >
                Agregar
              </Button>
            </Box>
            <List>
              {employee.observations?.map((obs, index) => (
                <Card key={index} sx={{ mb: 1 }}>
                  <CardContent>
                    <Typography variant="body2">{obs.text}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {obs.author?.name} - {format(new Date(obs.date), 'dd/MM/yyyy HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={sigDialogOpen} onClose={() => setSigDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nueva solicitud de firma</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Documento o concepto"
            value={newSigTitle}
            onChange={(e) => setNewSigTitle(e.target.value)}
            placeholder="Ej. Anexo de confidencialidad 2026"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSigDialogOpen(false)}>Cerrar</Button>
          <Button variant="contained" onClick={handleCreateSignature} disabled={!newSigTitle.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <PhotoCapture
        open={openCamera}
        onClose={() => setOpenCamera(false)}
        onCapture={handlePhotoCapture}
      />
    </Container>
  );
};

export default EmployeeDetail;
